/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AnimatePresence, LayoutGroup } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Pill, FileText, Sparkles, Download, Stethoscope } from 'lucide-react';
import { listMyDocuments } from '@/lib/medical';
import { listMyPrescriptions } from '@/lib/medical';
import { fhirEverything } from '@/lib/marketplace';
import { useAuth } from '@/lib/auth';
import { WalletCard } from '@/components/wallet/WalletCard';
import { UploadDialog } from '@/components/wallet/UploadDialog';
import { DetailDialog } from '@/components/wallet/DetailDialog';

// ---------- Main Wallet Page ----------
const WalletPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selected, setSelected] = useState(null); // { item, kind }
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, p] = await Promise.all([listMyDocuments(), listMyPrescriptions()]);
      setDocs(d.documents || []);
      setPrescriptions(p.prescriptions || []);
    } catch (e) {
      console.error('WalletPage: load failed', e);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const doFhirExport = async () => {
    if (!user?.id) return;
    setExporting(true);
    try {
      const bundle = await fhirEverything(user.id);
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/fhir+json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fhir-bundle-${user.id}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
      toast.success('Export FHIR completato');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore export FHIR');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="wallet-page">
      {/* Hero */}
      <div className="rounded-3xl bg-hero-gradient border border-border/60 p-6 md:p-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full glass text-[11px] font-semibold uppercase tracking-wider text-primary mb-3">
              <Sparkles className="h-3 w-3" /> {t('walletBadge')}
            </div>
            <h1 className="font-display font-bold text-2xl md:text-3xl tracking-tight">{t('walletTitleHero')}</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-lg">{t('walletSubtitleHero')}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="lg" variant="outline" onClick={doFhirExport} disabled={exporting} data-testid="wallet-fhir-export">
              <Download className="h-4 w-4" /> {exporting ? 'Export…' : 'Export FHIR'}
            </Button>
            <Button size="lg" onClick={() => setUploadOpen(true)} data-testid="wallet-add-btn">
              <Plus className="h-4 w-4" /> {t('walletAdd')}
            </Button>
          </div>
        </div>
      </div>

      <LayoutGroup>
        {/* Prescriptions section */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Pill className="h-4 w-4 text-primary" />
            <h2 className="font-display font-semibold text-lg">{t('walletPrescriptionsTitle')}</h2>
            <Badge variant="secondary" className="ml-1">{prescriptions.length}</Badge>
          </div>
          {(() => {
            if (loading) {
              return <div className="text-sm text-muted-foreground">{t('loading')}</div>;
            }
            if (prescriptions.length === 0) {
              return (
                <Card className="p-6 text-center">
                  <Stethoscope className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">{t('walletNoPrescriptions')}</p>
                </Card>
              );
            }
            return (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <AnimatePresence>
                  {prescriptions.map((p) => (
                    <WalletCard
                      key={p.id}
                      item={p}
                      kind="prescription"
                      onOpen={(item, kind) => setSelected({ item, kind })}
                    />
                  ))}
                </AnimatePresence>
              </div>
            );
          })()}
        </div>

        {/* Documents section */}
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-4 w-4 text-accent" />
            <h2 className="font-display font-semibold text-lg">{t('walletDocumentsTitle')}</h2>
            <Badge variant="secondary" className="ml-1">{docs.length}</Badge>
          </div>
          {(() => {
            if (loading) {
              return <div className="text-sm text-muted-foreground">{t('loading')}</div>;
            }
            if (docs.length === 0) {
              return (
                <Card className="p-6 text-center">
                  <FileText className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">{t('walletNoDocuments')}</p>
                  <Button size="sm" onClick={() => setUploadOpen(true)} className="mt-3">
                    {t('walletAdd')}
                  </Button>
                </Card>
              );
            }
            return (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <AnimatePresence>
                  {docs.map((d) => (
                    <WalletCard
                      key={d.id}
                      item={d}
                      kind="document"
                      onOpen={(item, kind) => setSelected({ item, kind })}
                    />
                  ))}
                </AnimatePresence>
              </div>
            );
          })()}
        </div>
      </LayoutGroup>

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onUploaded={load} />
      <DetailDialog item={selected?.item} kind={selected?.kind} onClose={() => setSelected(null)} onDeleted={load} />
    </div>
  );
};

export default WalletPage;
